import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  LinearProgress,
  Divider,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ReactComponent as UploadIcon } from "../../../../images/uploadIcon.svg";
import { Description, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import AddSourcePopup from "./AddAnotherSource";

import { uploadDocumentToTransformer } from "../../../../services/webServices";
import MessagePopup from "../../../popup-components/MessagePopup";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

const UploadDocuments = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [, setTick] = useState(0); // Force re-render every minute

  const navigate = useNavigate();

  // Update time display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const getTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);

    if (seconds < 60) return "just now";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const allowedFormats = [
      ".docx",
      ".pdf",
      ".png",
      ".jpeg",
      ".jpg",
      ".mp4",
      ".mp3",
      ".mov",
      ".avi",
      ".mkv",
      ".eml",
      ".msg",
      ".mbox",
    ];

    const invalidFiles = files.filter((file) => {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      return !allowedFormats.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      setErrorMessage(
        `Unsupported file format(s). Please check the file format.`
      );
      setIsErrorPopupOpen(true);
      return;
    }

    // Add timestamp to each file
    const filesWithTimestamp = files.map((file) => ({
      file,
      uploadedAt: Date.now(),
    }));

    setUploadedFiles((prev) => [...prev, ...filesWithTimestamp]);
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAnalyzeFiles = async () => {
    try {
      setIsAnalyzing(true);

      // Extract just the file objects for upload
      const files = uploadedFiles.map((item) => item.file);

      // Upload all files together in one request
      const uploadResponse = await uploadDocumentToTransformer(files);

      // Use the response data directly
      const resultData = uploadResponse.data.structuredCSV;
      console.log("resultData is---->", resultData);

      // Navigate with the result data
      navigate("/dashboard/qe-strategy", {
        state: { outputData: resultData },
      });
    } catch (error) {
      console.error("Unexpected error:", error);

      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setIsErrorPopupOpen(false);
      } else {
        // Show popup for all other errors
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(msg);
        setIsErrorPopupOpen(true);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
          maxWidth: "1400px",
          mx: "auto",
          mt: 2,
        }}
      >
        {/* Upload Area (Box 1) */}
        <Box
          sx={{
            backgroundColor: "#fff",
            boxShadow: "0 0 0 1px #f0f0f0",
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              mb: { xs: 1.5, sm: 2 },
              fontWeight: 500,
              fontSize: { xs: "1.1rem", sm: "1.125rem", md: "1.25rem" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Upload Files
          </Typography>

          <Box
            sx={{
              border: "2px dashed #99c5fa",
              backgroundColor: "#e9f3ff",
              borderRadius: 2,
              p: { xs: 2, sm: 3, md: 4 },
              textAlign: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: { xs: 1.5, sm: 2 },
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              <UploadIcon
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  color: "#5c9cfa",
                  mr: { xs: 0.5, sm: 1 },
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
              >
                Supported formats for upload
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.8rem", sm: "0.825rem" },
                mb: { xs: 2, sm: 2 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Drag and drop your .doc, .docx, .pdf, .png, .jpeg, .jpg, .mp3,
              .mp4, .eml, .msg, .mbox
            </Typography>

            {/* Choose Files Button */}
            <Button
              variant="contained"
              startIcon={
                <UploadIcon
                  sx={{
                    fontSize: { xs: 20, sm: 22 },
                    color: "white",
                  }}
                />
              }
              sx={{
                backgroundColor: "#3898fc",
                fontWeight: 500,
                height: { xs: 38, sm: 40 },
                px: { xs: 2.5, sm: 3 },
                borderRadius: 1,
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 140 },
              }}
              component="label"
            >
              Choose Files
              <input
                type="file"
                hidden
                multiple
                accept=".docx,.pdf,.png,.jpeg,.jpg,.mp4,.mp3,.mov,.avi,.mkv,.eml,.msg,.mbox"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
        </Box>

        {/* Uploaded Files Section (Box 2) */}
        {uploadedFiles.length > 0 && (
          <Box
            sx={{
              mt: { xs: 3, sm: 4 },
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0 0 0 1px #f0f0f0",
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: { xs: 1.5, sm: 2 },
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              Uploaded Files
            </Typography>

            <List sx={{ p: 0 }}>
              {uploadedFiles.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      px: { xs: 1, sm: 2 },
                      py: 1,
                      flexDirection: { xs: "row", sm: "row" },
                      alignItems: "center",
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(index)}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.06)",
                          },
                        }}
                      >
                        <Close sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                      <Description
                        sx={{
                          color: "#1495f3",
                          fontSize: { xs: 22, sm: 26 },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: 14, sm: 15 },
                            wordBreak: "break-all",
                          }}
                        >
                          {item.file.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        >
                          {formatFileSize(item.file.size)}{" "}
                          {getTimeAgo(item.uploadedAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>

            {isAnalyzing && (
              <>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Box sx={{ px: { xs: 0, sm: 1 } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                      mb: 1,
                      fontSize: { xs: "0.875rem", sm: "0.9rem" },
                    }}
                  >
                    Analyzing file...
                  </Typography>
                  <LinearProgress
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#eaf4fc",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        backgroundColor: "#329efc",
                      },
                    }}
                  />
                </Box>
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: { xs: 2, sm: 3 },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#329efc",
                  height: { xs: 38, sm: 40 },
                  width: { xs: "100%", sm: 160 },
                  minWidth: { sm: 120 },
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "&:hover": { backgroundColor: "#2887e2" },
                }}
                disabled={isAnalyzing || uploadedFiles.length === 0}
                endIcon={
                  <ArrowForwardIcon
                    sx={{
                      fontSize: { xs: 18, sm: 20 },
                    }}
                  />
                }
                onClick={handleAnalyzeFiles}
              >
                {isAnalyzing ? "Analyzing..." : "Analyze Files"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Footer Actions */}
        <Box
          sx={{
            width: "100%",
            mt: 3,
            mb: 3,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              onClick={() => setOpen(true)}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#0094FB",
                borderWidth: 1.5,
                color: "#0094FB",
                fontSize: { xs: "0.825rem", sm: "0.9rem" },
                fontWeight: 600,
                height: { xs: 34, sm: 36 },
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 160 },
                "&:hover": {
                  borderColor: "#0094FB",
                  backgroundColor: "rgba(0,148,251,0.04)",
                },
              }}
            >
              Add another source
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                backgroundColor: "#0094FB",
                textTransform: "none",
                width: { xs: "100%", sm: 100 },
                height: { xs: 34, sm: 36 },
                fontSize: { xs: "0.825rem", sm: "1rem" },
              }}
              endIcon={
                <ArrowForwardIcon
                  sx={{
                    fontSize: { xs: 18, sm: 20 },
                  }}
                />
              }
            >
              Next
            </Button>
          </Box>
        </Box>

        {isErrorPopupOpen && (
          <MessagePopup
            modalOpen={isErrorPopupOpen}
            okayHandler={() => setIsErrorPopupOpen(false)}
            message={errorMessage}
          />
        )}

        {sessionExpired && <SessionExpired />}
      </Box>

      {/* Popup Drawer */}
      <AddSourcePopup
        open={open}
        onClose={() => setOpen(false)}
        onFileUpload={(files) => {
          const filesWithTimestamp = files.map((file) => ({
            file,
            uploadedAt: Date.now(),
          }));
          setUploadedFiles((prev) => [...prev, ...filesWithTimestamp]);
        }}
      />
    </>
  );
};

export default UploadDocuments;
